import { Icon } from '@design/components/icon/Icon';
import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import clsx from 'clsx';
import React, { ButtonHTMLAttributes, ReactNode } from 'react';

import styles from './Button.module.scss';

export type Size = 'xsmall' | 'small' | 'medium' | 'large' | 'xl' | 'xxl';
export type IconPosition = 'left' | 'right';
export type Variant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'empty'
  | 'critical';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Forwarded ref to the underlying `<button>` element. */
  ref?: React.Ref<HTMLButtonElement>;
  /** Visible button text. Omit for icon-only buttons. */
  label?: ReactNode;
  /** Name of an icon from the design-system iconography set, rendered next to the label. */
  iconName?: SvgIconNames;
  /** Whether the icon sits before or after the label. Defaults to `'left'`. */
  iconPosition?: IconPosition;
  /**
   * Visual style: `'primary'` (filled accent), `'secondary'` (outlined),
   * `'tertiary'` (subtle), `'empty'` (text only, no chrome),
   * `'critical'` (destructive). Defaults to `'primary'`.
   */
  variant?: Variant;
  /** Size preset (controls height, padding, and icon size). Defaults to `'medium'`. */
  size?: Size;
  /** When true, replaces the label/icon with an inline spinner. */
  isLoading?: boolean;
  /** When true, the button stretches to fill its container. */
  fullWidth?: boolean;
  /** Test selector forwarded as `data-testid`. */
  'data-testid'?: string;
  /** Escape hatch for arbitrary content rendered after the label/spinner. Prefer `label`. */
  children?: ReactNode;
}

/**
 * Primary call-to-action component for click handlers that do **not**
 * navigate. For navigation use `ButtonLink` instead.
 *
 * Supports label-only, icon-only (omit `label`), and icon+label layouts.
 * Visual variants and sizes are driven entirely by `variant` and `size`.
 */
export const Button = ({
  label,
  iconName,
  iconPosition = 'left',
  variant = 'primary',
  size = 'medium',
  isLoading = false,
  fullWidth = false,
  className,
  children,
  ref,
  ...rest
}: ButtonProps) => {
  let iconSize: number;

  switch (size) {
    case 'large':
    case 'xl':
      iconSize = 20;
      break;
    case 'xxl':
      iconSize = 40;
      break;
    case 'xsmall':
      iconSize = 12;
      break;
    default:
      iconSize = 16;
  }

  return (
    <button
      data-button
      data-testid="button"
      className={clsx(styles.root, styles[variant], styles[size], className, {
        [styles.fullWidth]: fullWidth,
        [styles.iconOnly]: !label,
      })}
      {...rest}
      ref={ref}
    >
      {isLoading ? (
        <span className={styles.loading} data-testid="button-loading">
          <div className={styles.spinner} />
        </span>
      ) : (
        <span
          data-button-label
          className={clsx(styles.label, {
            [styles.reversed]: iconPosition === 'right',
          })}
        >
          {iconName && <Icon iconName={iconName} size={iconSize} />}
          {label && (
            <span data-button-text className={styles.text}>
              {label}
            </span>
          )}
        </span>
      )}
      {children}
    </button>
  );
};
